import { Button } from "components/DesignSystem/Button/Button";
import { Tag, TagColorType } from "components/DesignSystem/Tag/Tag";
import { Banner } from "components/DesignSystem/Banner/Banner";
import { TriangleWarning } from "components/icons/TriangleWarning";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useModal } from "hooks/useModal";
import { useToast } from "hooks/useToast";
import { useAppDispatch } from "hooks/useAppDispatch";
import { useRoleBasedView } from "hooks/useRoleBasedView";
import { useHistory, useLocation } from "react-router-dom";
import { useQuery } from "hooks/useQuery";
import dayjs from "dayjs";
import { useRef, useState } from "react";
import {
  StateRegistrationsRes,
  StateRegistrationNameType,
  stateRegistrationsApis,
  useUpdateStateRegistrationMutation,
  useGetStateRegistrationByIdQuery,
} from "store/apis/StateRegistrations";
import { BackendError } from "types/utils/error";
import NotApplicable from "static/images/NotApplicable.svg";
import ToolTip from "components/design/toolTip";
import Modal from "components/DesignSystem/Modal/Modal";
import { StateRegistrationCard } from "components/StateRegistrations/StateRegistrationCard";
import { REGISTRATION_TYPE_LABELS } from "./StateComplianceDashboard";
import { Form, Formik } from "formik";
import { FieldBuilder } from "components/Entity/EntityFormEngine/FieldBuilder";
import { EntityDataAttributeWithFieldKey } from "store/apis/registrations";
import { NotAllowed } from "components/icons/Chat/NotAllowed";

const isFileEmpty = (value: unknown) =>
  value === null ||
  value === undefined ||
  (Array.isArray(value) && value.length === 0);

type UploadCertificateModalProps = {
  isOpen: boolean;
  onClose: () => void;
  groupId: string;
  entityId: string;
  stateId: string;
  stateType: StateRegistrationNameType;
  fileAttr: EntityDataAttributeWithFieldKey;
};

const UploadCertificateModal = ({
  isOpen,
  onClose,
  groupId,
  entityId,
  stateId,
  stateType,
  fileAttr,
}: UploadCertificateModalProps) => {
  const [updateRegistration, { isLoading }] =
    useUpdateStateRegistrationMutation();
  const dispatch = useAppDispatch();
  const { alertToast, successToast } = useToast();

  const fieldKey = fileAttr.field_key || fileAttr.name;
  const initialValues = { [fieldKey]: fileAttr.value ?? [] };

  const handleSubmit = async (values: Record<string, unknown>) => {
    try {
      const updatedFileAttr = {
        ...fileAttr,
        value: values[fieldKey] as EntityDataAttributeWithFieldKey["value"],
      };
      await updateRegistration({
        groupId,
        entityId,
        stateId,
        payload: { data_attributes: [updatedFileAttr], type: stateType },
      }).unwrap();
      successToast({ message: "Certificate uploaded successfully!" });
      dispatch(
        stateRegistrationsApis.util.invalidateTags(["STATE_REGISTRATIONS"])
      );
      onClose();
    } catch (error) {
      alertToast({ message: (error as BackendError)?.data?.error?.message });
    }
  };

  return (
    <Modal.Root open={isOpen} onOpenChange={onClose}>
      <Modal.Content useCustomOverlay size="small">
        <Modal.Header>
          <Modal.Title>Upload certificate</Modal.Title>
          <Modal.Close />
        </Modal.Header>
        <Formik
          initialValues={initialValues}
          onSubmit={handleSubmit}
          enableReinitialize
        >
          {({ submitForm }) => (
            <Form className="all:unset t-flex t-flex-col t-gap-4">
              <div className="t-p-5">
                <FieldBuilder field={fileAttr} />
              </div>
              <Modal.FooterButtonGroup>
                <Modal.RawClose asChild>
                  <Button type="button" customType="secondary">
                    Cancel
                  </Button>
                </Modal.RawClose>
                <Button
                  customType="primary"
                  isLoading={isLoading}
                  disabled={isLoading}
                  onClick={submitForm}
                >
                  Upload
                </Button>
              </Modal.FooterButtonGroup>
            </Form>
          )}
        </Formik>
      </Modal.Content>
    </Modal.Root>
  );
};

type Props = {
  stateDetail: StateRegistrationsRes;
  raIsExpiringSoon: boolean;
  raExpirationDate: string | undefined;
  onRegisterNow: (type: StateRegistrationNameType) => void;
  onViewFiling: () => void;
  entityId: string;
};

const getRegistrationTagType = ({
  isWarn,
  isNA,
  isDone,
  isInCart,
  isFilingInProgress,
}: {
  isWarn: boolean;
  isNA: boolean;
  isDone: boolean;
  isInCart: boolean;
  isFilingInProgress: boolean;
}): TagColorType => {
  if (isWarn) return "yellow";
  if (isNA) return "gray";
  if (isDone) return "green";
  if (isFilingInProgress) return "orange";
  if (isInCart) return "purple";
  return "red";
};

export const RegistrationCards = ({
  stateDetail,
  raIsExpiringSoon,
  raExpirationDate,
  onRegisterNow,
  onViewFiling,
  entityId,
}: Props) => {
  const types = ["SECRETARY_OF_STATE", "REGISTERED_AGENT", "PAY_ROLL"] as const;
  const { uuid: groupId } = useCurrentGroupContext();
  const { isAdmin } = useRoleBasedView();
  const history = useHistory();
  const location = useLocation();
  const query = useQuery();
  const queryEntityId = query.get("entity") ?? entityId;

  const handleGoToCart = () => {
    const cartUrl = isAdmin
      ? `/admin/crm/${groupId}/billing/cart?entity=${queryEntityId}`
      : `/settings/billing/cart?entity=${queryEntityId}&settings_back_to=${encodeURIComponent(
          location.pathname + location.search
        )}`;
    history.push(cartUrl);
  };

  const sosIsDone = stateDetail.compliance_status.is_sos_done;

  const { data: sosDetail } = useGetStateRegistrationByIdQuery(
    {
      groupId: groupId!,
      entityId,
      stateId: stateDetail.state_id,
      stateType: "SECRETARY_OF_STATE",
    },
    { skip: !groupId || !sosIsDone }
  );

  const sosFileAttr = sosDetail?.data_attributes.find(
    (a) => (a.type === "FILE" || a.type === "MULTI_FILE_INPUT") && a.is_required
  );
  const sosCOIEmpty =
    sosFileAttr !== undefined && isFileEmpty(sosFileAttr.value);

  const payrollIsDone = stateDetail.compliance_status.is_payroll_done;

  const { data: payrollDetail } = useGetStateRegistrationByIdQuery(
    {
      groupId: groupId!,
      entityId,
      stateId: stateDetail.state_id,
      stateType: "PAY_ROLL",
    },
    { skip: !groupId || !payrollIsDone }
  );

  const payrollDetailFileAttr = payrollDetail?.data_attributes.find(
    (a) => (a.type === "FILE" || a.type === "MULTI_FILE_INPUT") && a.is_required
  );

  const alreadyRegisteredModal = useModal();
  const uploadCertModal = useModal();
  const [uploadCertState, setUploadCertState] = useState<{
    type: StateRegistrationNameType;
    fileAttr: EntityDataAttributeWithFieldKey;
  } | null>(null);
  const [selectedType, setSelectedType] =
    useState<StateRegistrationNameType | null>(null);
  const sosFormRef = useRef<{
    submitForm: () => void;
    isValid: boolean;
  } | null>(null);
  const raFormRef = useRef<{
    submitForm: () => void;
    isValid: boolean;
  } | null>(null);
  const [sosIsValid, setSosIsValid] = useState(false);
  const [raIsValid, setRaIsValid] = useState(false);
  const [revertingType, setRevertingType] =
    useState<StateRegistrationNameType | null>(null);
  const [updateRegistration, { isLoading: isUpdating }] =
    useUpdateStateRegistrationMutation();
  const dispatch = useAppDispatch();
  const { alertToast, successToast } = useToast();

  const openAlreadyRegistered = (type: StateRegistrationNameType) => {
    setSelectedType(type);
    alreadyRegisteredModal.open();
  };

  const handleNotNeeded = async (type: StateRegistrationNameType) => {
    if (!groupId) return;
    const reg = stateDetail[type];
    setSelectedType(type);
    try {
      await updateRegistration({
        groupId,
        entityId,
        stateId: stateDetail.state_id,
        payload: {
          data_attributes: reg.data_attributes,
          type,
          status: "NOT_APPLICABLE",
        },
      }).unwrap();
      successToast({ message: "Marked as not needed." });
      dispatch(
        stateRegistrationsApis.util.invalidateTags(["STATE_REGISTRATIONS"])
      );
    } catch (error) {
      alertToast({ message: (error as BackendError)?.data?.error?.message });
    } finally {
      setSelectedType(null);
    }
  };

  const handleRevert = async (type: StateRegistrationNameType) => {
    if (!groupId) return;
    const reg = stateDetail[type];
    setRevertingType(type);
    try {
      await updateRegistration({
        groupId,
        entityId,
        stateId: stateDetail.state_id,
        payload: {
          data_attributes: reg.data_attributes,
          type,
          status: "NOT_REGISTERED",
        },
      }).unwrap();
      successToast({ message: "Registration marked as needed." });
      dispatch(
        stateRegistrationsApis.util.invalidateTags(["STATE_REGISTRATIONS"])
      );
    } catch (error) {
      alertToast({ message: (error as BackendError)?.data?.error?.message });
    } finally {
      setRevertingType(null);
    }
  };

  const raReg = stateDetail["REGISTERED_AGENT"];
  const raStatus = raReg.status;
  const raIsDone =
    raStatus === "REGISTERED" ||
    raStatus === "INCORPORATED" ||
    raStatus === "FOREIGN_QUALIFIED";
  const raIsNA = raStatus === "NOT_APPLICABLE";

  const raTagType = getRegistrationTagType({
    isWarn: raIsExpiringSoon,
    isNA: raIsNA,
    isDone: raIsDone,
    isInCart: false,
    isFilingInProgress: false,
  });

  const raTagLabel = raIsExpiringSoon
    ? `Registered Agent · Expires ${dayjs(raExpirationDate).format("MMM D")}`
    : `Registered Agent · ${raReg.status_text}`;

  return (
    <>
      <div className="t-flex t-flex-col t-divide-y t-divide-neutral-10">
        {types.map((type) => {
          const reg = stateDetail[type];
          const s = reg.status;
          const isDone = s === "REGISTERED" || reg.cart_status === "COMPLETED";
          const isNA = s === "NOT_APPLICABLE";
          const isRAWarn = type === "REGISTERED_AGENT" && raIsExpiringSoon;
          const isInCart = reg.cart_status === "IN_CART";
          const isFilingInProgress =
            !stateDetail.is_home_state &&
            !isDone &&
            ((type === "SECRETARY_OF_STATE" &&
              stateDetail.has_sos_task_launched) ||
              (type === "PAY_ROLL" &&
                stateDetail.has_payroll_intake &&
                stateDetail.has_payroll_task_launched));
          const showGoToCart =
            isInCart && !isDone && !isNA && !isFilingInProgress;
          const showRegisterBtn =
            !isDone && !isNA && !isInCart && !isFilingInProgress;
          const showNotApplicableBtn =
            showRegisterBtn &&
            !(type === "REGISTERED_AGENT" && stateDetail.is_home_state);

          const payrollDocAttr =
            type === "PAY_ROLL"
              ? reg.data_attributes.find(
                  (attr) =>
                    (attr.type === "FILE" ||
                      attr.type === "MULTI_FILE_INPUT") &&
                    attr.is_required
                )
              : null;

          let hasPendingDoc = false;
          if (type === "SECRETARY_OF_STATE") {
            hasPendingDoc = isDone && sosCOIEmpty;
          } else if (type === "PAY_ROLL") {
            hasPendingDoc =
              isDone &&
              Boolean(payrollDocAttr) &&
              isFileEmpty(payrollDocAttr!.value);
          }

          const tagType = getRegistrationTagType({
            isWarn: isRAWarn,
            isNA,
            isDone,
            isInCart,
            isFilingInProgress,
          });

          const isTaskCompleted =
            s !== "REGISTERED" && reg.cart_status === "COMPLETED";

          const statusLabel = isFilingInProgress
            ? "Filing in progress"
            : isRAWarn
            ? `Expires ${dayjs(raExpirationDate).format("MMM D")}`
            : isInCart
            ? "In cart"
            : isTaskCompleted
            ? "Registered"
            : reg.status_text;

          const isMarkingNotNeeded =
            isUpdating && selectedType === type && !isNA;
          const isReverting = revertingType === type;

          if (type === "REGISTERED_AGENT") return null;

          return (
            <div
              key={type}
              className="t-flex t-flex-col t-gap-3 t-p-5 sm:t-p-4"
            >
              <div className="t-flex t-items-start t-gap-3 sm:t-flex-col">
                <div className="t-flex-1">
                  <div className="t-flex t-items-center t-gap-2 t-flex-wrap">
                    <span className="t-text-body t-text-text-60">
                      {REGISTRATION_TYPE_LABELS[type]}
                    </span>
                    <Tag tagType={tagType} size="small" icon={false} rounded>
                      {statusLabel}
                    </Tag>
                    {type === "SECRETARY_OF_STATE" &&
                      (raIsDone || raIsNA || raIsExpiringSoon) && (
                        <Tag
                          tagType={raTagType}
                          size="small"
                          icon={false}
                          rounded
                        >
                          {raIsExpiringSoon ? raTagLabel : "Registered Agent"}
                        </Tag>
                      )}
                  </div>
                  {reg.purpose && (
                    <div className="t-text-body-sm-lite t-text-neutral-40 t-mt-0.5">
                      {reg.purpose}
                    </div>
                  )}
                </div>

                {(showRegisterBtn || showGoToCart || isFilingInProgress) && (
                  <div className="t-flex t-flex-col t-items-end t-gap-3 t-shrink-0 sm:t-items-start">
                    <div className="t-flex t-items-center t-gap-1.5">
                      {isFilingInProgress ? (
                        <Button
                          size="small"
                          customType="primary_outlined"
                          onClick={() =>
                            reg.task_uuid
                              ? history.push(`/tax/filings/${reg.task_uuid}`)
                              : onViewFiling()
                          }
                        >
                          View filing
                        </Button>
                      ) : showGoToCart ? (
                        <Button
                          size="small"
                          customType="primary_outlined"
                          onClick={handleGoToCart}
                        >
                          Go to cart
                        </Button>
                      ) : (
                        <Button
                          size="small"
                          customType="primary_outlined"
                          onClick={() => onRegisterNow(type)}
                        >
                          Register now
                        </Button>
                      )}
                      {showNotApplicableBtn && (
                        <ToolTip text="Not needed">
                          <Button
                            size="small"
                            customType="icon"
                            isLoading={isMarkingNotNeeded}
                            disabled={isMarkingNotNeeded}
                            onClick={() => handleNotNeeded(type)}
                          >
                            <NotAllowed />
                          </Button>
                        </ToolTip>
                      )}
                    </div>
                    {!isFilingInProgress && (
                      <span className="t-text-body-sm-italic t-italic t-text-text-30">
                        Registered?{" "}
                        <Button
                          size="xsmall"
                          customType="link"
                          className="!t-text-body-sm-italic !t-italic"
                          onClick={() => openAlreadyRegistered(type)}
                        >
                          Add details
                        </Button>
                      </span>
                    )}
                  </div>
                )}

                {isNA && (
                  <Button
                    size="xsmall"
                    customType="ghost"
                    isLoading={isReverting}
                    disabled={isReverting}
                    onClick={() => handleRevert(type)}
                  >
                    Mark as needed
                  </Button>
                )}
              </div>

              {hasPendingDoc && (
                <Banner
                  type="warning"
                  icon={
                    <span className="t-flex t-shrink-0 t-text-orange">
                      <TriangleWarning />
                    </span>
                  }
                  description="Document submission pending"
                  cta={
                    <Button
                      size="xsmall"
                      customType="secondary"
                      onClick={() => {
                        const fileAttr =
                          type === "SECRETARY_OF_STATE"
                            ? sosFileAttr
                            : payrollDetailFileAttr;
                        if (!fileAttr) return;
                        setUploadCertState({ type, fileAttr });
                        uploadCertModal.open();
                      }}
                    >
                      Upload certificate
                    </Button>
                  }
                />
              )}
            </div>
          );
        })}
      </div>

      <Modal.Root
        open={alreadyRegisteredModal.isOpen}
        onOpenChange={alreadyRegisteredModal.close}
      >
        <Modal.Content useCustomOverlay size="regular">
          <Modal.Header>
            <Modal.Title>
              {selectedType ? REGISTRATION_TYPE_LABELS[selectedType] : ""}
            </Modal.Title>
            <Modal.Close />
          </Modal.Header>
          {selectedType && (
            <div className="t-overflow-y-auto">
              <StateRegistrationCard
                key={selectedType}
                stateId={stateDetail.state_id}
                stateType={selectedType}
                stateName={stateDetail.state}
                flat
                startInEditMode
                hideAlreadyRegisteredLabel
                hideInnerBorder
                onLetInkleHandle={() => {}}
                hideBottomActions
                formikRef={sosFormRef}
                onValidityChange={setSosIsValid}
                onSaveSuccess={() => {
                  if (selectedType !== "SECRETARY_OF_STATE") {
                    alreadyRegisteredModal.close();
                  }
                }}
              />
              {selectedType === "SECRETARY_OF_STATE" && (
                <>
                  <div className="t-px-5 t-py-3 t-text-subtext t-font-medium t-text-text-100 t-border-0 t-border-t t-border-solid t-border-neutral-10">
                    {REGISTRATION_TYPE_LABELS["REGISTERED_AGENT"]}
                  </div>
                  <StateRegistrationCard
                    key="REGISTERED_AGENT"
                    stateId={stateDetail.state_id}
                    stateType="REGISTERED_AGENT"
                    stateName={stateDetail.state}
                    flat
                    startInEditMode
                    hideAlreadyRegisteredLabel
                    hideInnerBorder
                    onLetInkleHandle={() => {}}
                    hideBottomActions
                    formikRef={raFormRef}
                    onValidityChange={setRaIsValid}
                    onSaveSuccess={() => alreadyRegisteredModal.close()}
                  />
                </>
              )}
            </div>
          )}
          <Modal.FooterButtonGroup>
            <Modal.RawClose asChild>
              <Button type="button" customType="secondary">
                Cancel
              </Button>
            </Modal.RawClose>
            <Button
              customType="primary"
              disabled={
                selectedType === "SECRETARY_OF_STATE"
                  ? !sosIsValid || !raIsValid
                  : !sosIsValid
              }
              onClick={() => {
                sosFormRef.current?.submitForm();
                if (selectedType === "SECRETARY_OF_STATE") {
                  raFormRef.current?.submitForm();
                }
              }}
            >
              Save changes
            </Button>
          </Modal.FooterButtonGroup>
        </Modal.Content>
      </Modal.Root>

      {uploadCertState && groupId && (
        <UploadCertificateModal
          isOpen={uploadCertModal.isOpen}
          onClose={() => {
            uploadCertModal.close();
            setUploadCertState(null);
          }}
          groupId={groupId}
          entityId={entityId}
          stateId={stateDetail.state_id}
          stateType={uploadCertState.type}
          fileAttr={uploadCertState.fileAttr}
        />
      )}
    </>
  );
};
